import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Globe,
  Server,
  FileCheck,
  Zap,
  Play,
  ArrowRight,
  AlertCircle,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewScan({ user, logout, token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    scan_type: "",
    target_url: "",
    target_ip: "",
    scan_depth: "standard",
    compliance_frameworks: []
  });

  const scanTypes = [
    { id: "web", label: "Web Application", icon: Globe, description: "Scan web applications for OWASP vulnerabilities" },
    { id: "network", label: "Network Infrastructure", icon: Server, description: "Analyze network security configurations" },
    { id: "compliance", label: "Compliance Only", icon: FileCheck, description: "Run compliance checks without vulnerability scanning" },
    { id: "full", label: "Full Assessment", icon: Zap, description: "Complete security assessment with all checks" }
  ];

  const frameworks = [
    { id: "HIPAA", name: "HIPAA", description: "Health Insurance Portability and Accountability Act" },
    { id: "SOC2", name: "SOC 2", description: "Service Organization Control 2" },
    { id: "NIST", name: "NIST", description: "National Institute of Standards and Technology" },
    { id: "OWASP", name: "OWASP ASVS", description: "Application Security Verification Standard" },
    { id: "CIS", name: "CIS Benchmarks", description: "Center for Internet Security" },
    { id: "HITRUST", name: "HITRUST CSF", description: "Health Information Trust Alliance" }
  ];

  const handleFrameworkToggle = (frameworkId) => {
    setFormData(prev => ({
      ...prev,
      compliance_frameworks: prev.compliance_frameworks.includes(frameworkId)
        ? prev.compliance_frameworks.filter(id => id !== frameworkId)
        : [...prev.compliance_frameworks, frameworkId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.scan_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.scan_type === 'web' && !formData.target_url) {
      toast.error("Please provide a target URL for web scanning");
      return;
    }

    if (formData.scan_type === 'network' && !formData.target_ip) {
      toast.error("Please provide a target IP for network scanning");
      return;
    }

    if ((formData.scan_type === 'compliance' || formData.scan_type === 'full') && formData.compliance_frameworks.length === 0) {
      toast.error("Please select at least one compliance framework");
      return;
    }

    setLoading(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        name: formData.name,
        description: formData.description,
        config: {
          scan_type: formData.scan_type,
          target_url: formData.target_url || null,
          target_ip: formData.target_ip || null,
          compliance_frameworks: formData.compliance_frameworks,
          scan_depth: formData.scan_depth
        }
      };

      const response = await axios.post(`${BACKEND_URL}/api/scans`, payload, { 
        headers, 
        withCredentials: true 
      });

      toast.success("Scan started successfully!");
      navigate(`/scan/${response.data.scan_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start scan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="new-scan-page">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold font-[Chivo]">New Security Scan</h2>
          <p className="text-muted-foreground">
            Configure and launch a new security assessment
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-[Chivo]">Scan Details</CardTitle>
              <CardDescription>Basic information about your security scan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Scan Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Patient Portal Q4 Assessment"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="scan-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description of the scan scope and objectives"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="scan-description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Scan Type */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-[Chivo]">Scan Type *</CardTitle>
              <CardDescription>Select the type of security assessment to perform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {scanTypes.map((type) => (
                  <motion.div
                    key={type.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card
                      className={`cursor-pointer transition-all ${
                        formData.scan_type === type.id
                          ? 'border-primary bg-primary/5 glow-primary'
                          : 'border-border hover:border-primary/30'
                      }`}
                      onClick={() => setFormData({ ...formData, scan_type: type.id })}
                      data-testid={`scan-type-${type.id}`}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${
                            formData.scan_type === type.id ? 'bg-primary/20' : 'bg-accent'
                          }`}>
                            <type.icon className={`h-5 w-5 ${
                              formData.scan_type === type.id ? 'text-primary' : 'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium">{type.label}</p>
                            <p className="text-sm text-muted-foreground">{type.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Target Configuration */}
          {(formData.scan_type === 'web' || formData.scan_type === 'full') && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-[Chivo]">Web Target</CardTitle>
                <CardDescription>Configure the target URL for web application scanning</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="target_url">Target URL *</Label>
                  <Input
                    id="target_url"
                    placeholder="https://app.healthcare.org"
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    data-testid="target-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the base URL of the web application to scan
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {(formData.scan_type === 'network' || formData.scan_type === 'full') && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-[Chivo]">Network Target</CardTitle>
                <CardDescription>Configure the target IP or range for network scanning</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="target_ip">Target IP/Range *</Label>
                  <Input
                    id="target_ip"
                    placeholder="192.168.1.0/24 or 10.0.0.1"
                    value={formData.target_ip}
                    onChange={(e) => setFormData({ ...formData, target_ip: e.target.value })}
                    data-testid="target-ip-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter an IP address or CIDR range to scan
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compliance Frameworks */}
          {(formData.scan_type === 'compliance' || formData.scan_type === 'full') && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-[Chivo]">Compliance Frameworks *</CardTitle>
                <CardDescription>Select which compliance frameworks to assess against</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {frameworks.map((framework) => (
                    <div
                      key={framework.id}
                      className={`flex items-start space-x-3 p-4 border rounded-sm cursor-pointer transition-all ${
                        formData.compliance_frameworks.includes(framework.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/30'
                      }`}
                      onClick={() => handleFrameworkToggle(framework.id)}
                      data-testid={`framework-${framework.id}`}
                    >
                      <Checkbox
                        checked={formData.compliance_frameworks.includes(framework.id)}
                        onCheckedChange={() => handleFrameworkToggle(framework.id)}
                      />
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{framework.name}</p>
                        <p className="text-sm text-muted-foreground">{framework.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scan Depth */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-[Chivo]">Scan Configuration</CardTitle>
              <CardDescription>Additional scan settings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="scan_depth">Scan Depth</Label>
                <Select
                  value={formData.scan_depth}
                  onValueChange={(value) => setFormData({ ...formData, scan_depth: value })}
                >
                  <SelectTrigger data-testid="scan-depth-select">
                    <SelectValue placeholder="Select scan depth" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quick">Quick (5-10 min)</SelectItem>
                    <SelectItem value="standard">Standard (15-30 min)</SelectItem>
                    <SelectItem value="deep">Deep (1-2 hours)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Deeper scans are more thorough but take longer to complete
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Warning */}
          <Card className="bg-yellow-500/5 border-yellow-500/20">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-500">Important Notice</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ensure you have proper authorization to scan the target systems. 
                    Unauthorized scanning may violate laws and regulations. All scan 
                    activities are logged for audit purposes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex items-center justify-between">
            <Button variant="outline" type="button" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="glow-primary" 
              disabled={loading || !formData.scan_type}
              data-testid="start-scan-btn"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Starting Scan...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Start Security Scan
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
